/*
  Klaviyo sign-ups (email, then optional first name and phone) for the TV,
  the footer and sold-out products. Uses Klaviyo's client API with the
  store's public key (Theme settings > Klaviyo), which is meant to sit in
  website code. Never put a private key (pk_...) anywhere in the theme.

  window.zpKlaviyo.subscribe({ key, list, email, phone, firstName, source, properties })
    adds someone to the list. Resolves to { ok: true } or { ok: false, detail };
    throws only when Klaviyo can't be reached (offline, ad blockers).
  window.zpKlaviyo.updateProfile({ key, email, firstName })
    adds a first name to someone already signed up.
  window.zpKlaviyo.toPhone(text, country) turns what someone typed into
    +17205550199 form, or null if it can't be a phone number. country is the
    country code box ('+1' unless they changed it). +1 numbers have to look
    like real US / Canada numbers; other countries only get a length check.
    Texts from a US toll-free number only reach +1 numbers, so others are
    collected but can't be texted yet. It can't tell whether a number is
    actually in service; that needs a paid lookup or a confirmation text.
  window.zpKlaviyo.formatPhone(text, country) formats it while typing:
    (720) 555-0199 for +1, just the digits for other countries.
  window.zpKlaviyo.countryCode(text) cleans the country code box: '+' and up
    to three digits.
  window.zpKlaviyo.flow({ key, list, source, askName, askPhone })
    the sign-up steps every form shares: 'email' -> 'name' -> 'phone' -> 'done'
    (name and phone only when asked; both optional). flow.step is the current
    step; flow.submit(value) sends what was typed for it and resolves to
    { ok: true, step } (the next step) or { ok: false, error: 'email' | 'phone' }.
    flow.submit('') on name or phone is the same as SKIP. The email step throws
    when Klaviyo can't be reached, so the form can fall back to Shopify.
*/
(() => {
  const REVISION = '2026-07-15';
  const headers = { 'Content-Type': 'application/vnd.api+json', Accept: 'application/vnd.api+json', revision: REVISION };

  // North American numbers follow fixed rules, so obviously made-up ones (1111111111, 2222222222,
  // 555-01xx, a 911-style area code) can be turned away before they reach Klaviyo.
  const TOLL_FREE = ['800', '833', '844', '855', '866', '877', '888'];
  const countryCode = (text) => `+${String(text || '').replace(/\D/g, '').slice(0, 3)}`;
  const codeDigits = (country) => String(country || '+1').replace(/\D/g, '') || '1';

  const toPhone = (text, country) => {
    const code = codeDigits(country);
    let digits = String(text || '').replace(/\D/g, '');
    if (code !== '1') {
      // Other countries: a length check only. A leading 0 (the local prefix, like UK 07...) is dropped.
      digits = digits.replace(/^0+/, '');
      const full = code + digits;
      if (digits.length < 6 || full.length < 8 || full.length > 15 || /^(\d)\1+$/.test(digits)) return null;
      return `+${full}`;
    }
    if (digits.length === 11 && digits[0] === '1') digits = digits.slice(1);
    if (digits.length !== 10) return null;
    const area = digits.slice(0, 3);
    const exchange = digits.slice(3, 6);
    if (!/^[2-9][0-8]\d$/.test(area) || area.endsWith('11') || TOLL_FREE.includes(area)) return null;
    if (!/^[2-9]\d\d$/.test(exchange) || exchange.endsWith('11')) return null;
    if (exchange === '555' && digits.slice(6, 8) === '01') return null;
    if (/^(\d)\1{9}$/.test(digits)) return null;
    return `+1${digits}`;
  };

  const formatPhone = (text, country) => {
    if (codeDigits(country) !== '1') return String(text || '').replace(/[^\d ]/g, '').slice(0, 18);
    let digits = String(text || '').replace(/\D/g, '');
    if (digits[0] === '1') digits = digits.slice(1);
    digits = digits.slice(0, 10);
    if (!digits) return '';
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const send = async (path, key, body) => {
    const response = await fetch(`https://a.klaviyo.com/client/${path}?company_id=${encodeURIComponent(key)}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });
    if (response.ok) return { ok: true };
    let detail = '';
    try {
      const data = await response.json();
      detail = (data.errors || []).map((error) => error.detail).join(' ');
    } catch (error) {}
    return { ok: false, detail };
  };

  const subscribe = ({ key, list, email, phone, firstName, source, properties }) => {
    const subscriptions = { email: { marketing: { consent: 'SUBSCRIBED' } } };
    const attributes = {
      email,
      subscriptions,
      properties: Object.assign({ 'Signup source': source }, properties || {}),
    };
    if (firstName) attributes.first_name = firstName;
    if (phone) {
      attributes.phone_number = phone;
      subscriptions.sms = { marketing: { consent: 'SUBSCRIBED' } };
    }
    return send('subscriptions', key, {
      data: {
        type: 'subscription',
        attributes: { custom_source: source, profile: { data: { type: 'profile', attributes } } },
        relationships: { list: { data: { type: 'list', id: list } } },
      },
    });
  };

  const updateProfile = ({ key, email, firstName }) =>
    send('profiles', key, { data: { type: 'profile', attributes: { email, first_name: firstName } } });

  const flow = ({ key, list, source, askName, askPhone }) => {
    const order = ['email', askName && 'name', askPhone && 'phone', 'done'].filter(Boolean);
    const next = (from) => order[order.indexOf(from) + 1];
    let email = '';
    const state = { step: 'email' };

    // options.country: the country code box on the phone step ('+1' unless changed).
    state.submit = async (value, options) => {
      const typed = String(value || '').trim();
      if (state.step === 'email') {
        const result = await subscribe({ key, list, email: typed, source });
        if (!result.ok) return { ok: false, error: 'email' };
        email = typed;
        state.step = next('email');
        return { ok: true, step: state.step };
      }
      if (state.step === 'name') {
        // A name that doesn't save isn't worth bothering anyone about: the email is already in.
        if (typed) {
          try {
            await updateProfile({ key, email, firstName: typed });
          } catch (error) {}
        }
        state.step = next('name');
        return { ok: true, step: state.step };
      }
      if (state.step === 'phone') {
        if (!typed) {
          state.step = 'done';
          return { ok: true, step: state.step };
        }
        const phone = toPhone(typed, options && options.country);
        if (!phone) return { ok: false, error: 'phone' };
        let result = { ok: false };
        try {
          result = await subscribe({ key, list, email, phone, source });
        } catch (error) {}
        if (!result.ok) return { ok: false, error: 'phone' };
        state.step = 'done';
        return { ok: true, step: state.step };
      }
      return { ok: true, step: state.step };
    };

    return state;
  };

  window.zpKlaviyo = { subscribe, updateProfile, toPhone, formatPhone, countryCode, flow };
})();
